import { useRouter } from "next/router"
import { NextSeo } from "next-seo"
import getConfig from "next/config"
import moment from "moment"
import { FEATURE_HIDE_IN_PRODUCTION_ARTICLE } from "lacinetek-common/src/metadata"

import { useTranslation } from "lib/i18n/translation"
import { useCountry } from "lib/country"
import Gtm from "lib/gtm"
import { getMainTag } from "lib/tools/blogs"
import isBrowser from "lib/tools/is-browser"

import BlogCategory from "components/common/blog-category"
import { baseBlogCategory, baseArticle } from "components/common/skeleton"
import Image from "components/common/image"

import NotFound from "../../404"

const { publicRuntimeConfig } = getConfig()

const Post = ({ slug, categorySlug, status }) => {
	return (
		<BlogCategory
			categorySlug={categorySlug}
			complete={true}
			renderLoading={() => (
				<Content
					article={baseArticle}
					category={baseBlogCategory}
					loading={true}
				/>
			)}
		>
			{category => {
				const article = category.posts.items.find(p => p.linkRewrite == slug)
				const title = article.metaTitle || article.title

				if (
					publicRuntimeConfig.lacinetekEnv === "production"
					&& title.includes(FEATURE_HIDE_IN_PRODUCTION_ARTICLE)
				) {
					if (!isBrowser && status) {
						status(404)
					}

					return <NotFound />
				}

				return <Content article={article} category={category} />
			}}
		</BlogCategory>
	)
}

const Content = ({ article, category, loading }) => {

	const { c } = useTranslation("common")
	const { asPath } = useCountry()
	const router = useRouter()
	const url = router ? router.asPath : null
	const pageTitle=loading
		? null
		: `${article.metaTitle || article.title} - ${category.metaTitle || category.name.replace(/\*\*/g, "")}`

	return (
		<>
			{loading
				? null
				: <>
						<Gtm pageType="Blog" pagePath={url} pageTitle={pageTitle} />
						<NextSeo
							title={pageTitle}
							description={article.metaDescription || article.descriptionShort}
							keywords={article.metaKeywords}
							canonical={asPath}
							openGraph={{
								type: "article",
								title: article.metaTitle || article.name,
								description: article.metaDescription || article.descriptionShort,
								url: asPath,
								images: [{ url: article.cover }],
								image: { url: article.cover }
							}}
						/>
				</>
			}
			<section className="focus front">
				<div className="infos">
					<div className={`post-infos ${loading ? "skeleton" : ""}`}>
						<ul className="tags">
							<li>{getMainTag(article)}</li>
						</ul>
						<span className="date">{moment(article.dateIssue).format("D MMM YYYY", "fr")}</span>
					</div>
					<h1 className={`title ${loading ? "skeleton" : ""}`}>{article.title}</h1>
				</div>
				<figure className={`background ${loading ? "skeleton" : ""}`}>
					<picture>
						<Image src={article.cover} alt={article.title} />
					</picture>
				</figure>
			</section>
			<section className="splitted large">
				<div className="content">
					<p className={`excerpt ${loading ? "skeleton" : ""}`}>{article.descriptionShort}</p>
					<div className={loading ? "skeleton" : ""} dangerouslySetInnerHTML={{ __html: article.description }} />
				</div>
				<aside>
					<nav className={`buttons small ${loading ? "skeleton" : ""}`}>
						{article.metaKeywords.split(",").map(tag => (
							<a href="#" onClick={e => { e.preventDefault(); }} rel="nofollow noopener" title={c("keyword")}>{tag}</a>
						))}
					</nav>
				</aside>
			</section>
		</>
	)
}

Post.getInitialProps = async (ctx) => {
	return {
		slug: ctx.query.id,
		categorySlug: ctx.query.category,
		status: ctx.res ? ctx.res.status.bind(ctx.res) : undefined
	}
}

export default Post
